import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import styled from "@emotion/styled";
import { SmallText, FlexRow, MediumText } from "../../../components/core/Utility";
import { ColumnHeader } from "../../components/core/Table/ColumnHeader";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { css } from "@emotion/react";
import _ from "lodash";
import { Checkbox } from "primereact/checkbox";
import { CUSTOM_FILTER_TYPE } from "../../../constants/ApplicationConstants";
import { Wrapper } from "../../../components/core/Table/GridStyles";
import { Button } from "@vds/buttons";
import { Tooltip } from "primereact/tooltip";
import FailedIcon from "../../assets/icons/ErrorIcon.svg";
import { Input } from "@vds/inputs";
import { validateVin } from "../../services/C4S Decommission - Replacement/validateVin";
import { VIN_REGEX } from "../../constants/C4SValidationConstants";

const rowsDefault = [{ row: "" }];
const colsDefault = [{ cols: "" }];

const GridWrapper = styled(Wrapper)`
  .p-column-title {
    margin-right: 0.2rem !important;
  }
  .p-disabled {
    background-color: lightgrey !important;
    border: 1px solid lightgrey !important;
  }
  .p-datatable .p-datatable-tbody > tr {
    background-color: transparent !important;
  }
  .p-datatable .p-datatable-footer {
    background-color: transparent !important;
  }
  .p-datatable-wrapper {
    scrollbar-width: none;
  }
`;

const SearchInput = styled.input`
  height: 25px;
  width: 150px;
  margin-right: 0.5rem;
`;
const IconWrapper = styled.div`
  height: 24px;
  align-items: center;
  cursor: pointer;
  margin-right: 5px;
  display: flex;
`;

export const AddVehicleAssignmentGrid = ({
  /** message in case of no records*/
  noDataMessage = "No data to display",
  /** data */
  rows = { rowsDefault },
  columns = { colsDefault },
  border,
  whiteSpace,
  flyOutMenu,
  resizableColumns = false,
  initialRows = rows,
  searchKeyboardValue = "",
  updatedVehicles,
  setUpdatedVehicles,
  showActionIcon,
  handleSaveClick,
  addMoreVehicles,
  pendingVehicleCount,
  deleteAll,
  allowedVins = [],
  setAllowedVins,
  payload = {},
}) => {
  const dt = useRef(null);
  const [filters, setFilters] = useState(null);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [vinInfo, setVinInfo] = useState({ validVIns: 0, errorVins: 0 });
  useEffect(() => {
    initFilters();
  }, []);

  useEffect(() => {
    if (searchKeyboardValue.length > 0) {
      const value = searchKeyboardValue;
      let updatedFilters = { ...filters };
      updatedFilters["global"].value = value;
      setFilters(updatedFilters);
      setGlobalFilterValue(value);
    } else {
      initFilters();
    }
  }, [searchKeyboardValue]);

  useEffect(() => {
    setUpdatedRows(rows);
    let validVIns = rows.filter((row) => row.error === false);
    let errorVins = rows.filter((row) => row.error === true);
    setVinInfo({ validVIns: validVIns.length, errorVins: errorVins.length });
  }, [rows]);

  useEffect(() => {
    determineSaveEnabled();
  }, [updatedRows, initialRows]);

  const determineSaveEnabled = () => {
    if (updatedVehicles.some((row) => row.error === true) || updatedRows.length === 0) {
      setSaveDisabled(true);
    } else {
      setSaveDisabled(false);
    }
  };

  const initFilters = () => {
    const obj = Object.fromEntries(
      columns.map((col) => {
        if (col.isDate) {
          return [col.value, { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] }];
        } else if (col.customFilterType === CUSTOM_FILTER_TYPE.dropDown || col.customFilterType === CUSTOM_FILTER_TYPE.listBox) {
          return [col.value, { value: null, matchMode: FilterMatchMode.CONTAINS }];
        } else {
          return [col.value, { operator: FilterOperator.AND, constraints: [{ value: null }] }];
        }
      })
    );
    obj["global"] = { value: null, matchMode: "customGlobalFilter" };
    setFilters(obj);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let updatedFilters = { ...filters };
    updatedFilters["global"].value = value;
    setFilters(updatedFilters);
    setGlobalFilterValue(value);
  };

  const updateRowData = (e, rowData, field) => {
    let newRows = [...updatedRows];
    let index = newRows.findIndex((row) => row.ind === rowData.ind && row.vin === rowData.vin);
    if (index !== -1) {
      newRows[index][field] = e.checked;
      setUpdatedRows(newRows);
      setUpdatedVehicles(newRows);
    }
  };

  const handleVin = async (e, rowData, field) => {
    const enteredVin = e.target.value;
    const isDuplicateVin = allowedVins.includes(enteredVin);
    let newRows = [...updatedRows];
    let index = newRows.findIndex((row) => row.vin === rowData.vin);
    newRows[index][field] = enteredVin;
    if (enteredVin.length === 17 && VIN_REGEX.test(enteredVin) && !isDuplicateVin) {
      let response = await validateVin({ vinList: [e.target.value], ...payload });
      if (response.data.statusCode == 200 && !isDuplicateVin) {
        if (response.data.data.validVins.length > 0) {
          let data = response.data.data.validVins[0];
          newRows[index]["error"] = false;
          newRows[index]["year"] = data.year;
          newRows[index]["make"] = data.make;
          newRows[index]["model"] = data.model;
          newRows[index]["weightClass"] = data.weightClass;
          newRows[index]["fuelType"] = data.fuelType;
          newRows[index]["errorMessage"] = "";
          setVinInfo({
            validVIns: newRows.filter((row) => row.error === false).length,
            errorVins: newRows.filter((row) => row.error === true).length,
          });
          setAllowedVins(newRows.map((row) => row.vin));
        } else if (response.data.data.incompatibleVins && response.data.data.incompatibleVins.length > 0) {
          newRows[index]["errorMessage"] = "Incompatible for HD";
        }
      }
    } else if (isDuplicateVin) {
      newRows[index]["errorMessage"] = "Duplicate VIN";
    } else {
      newRows[index]["errorMessage"] = "Error in VIN";
    }
    setUpdatedRows(newRows);
    setUpdatedVehicles(newRows);
  };

  const getBodyToDisplay = (rowData, data) => {
    let colAttributes = columns.find((col) => col.value == data.field);
    if (colAttributes.isCheckboxes) {
      return renderCheckboxes(rowData, data);
    } else {
      return renderContentForNonHyperlink(rowData, data);
    }
  };

  const renderContentForNonHyperlink = (rowData, data) => {
    if (data.field === "vin" && rowData["error"] === true) {
      return (
        <Input
          type="text"
          value={rowData[data.field]}
          style={{
            fontSize: "12px",
            width: "160px",
            height: "20px",
            color: rowData["error"] ? "red" : "black",
            padding: "3px 3px 3px 3px !important",
          }}
          aria-label="vin"
          aria-required="true"
          error={false}
          errorText="Invalid VIN"
          onChange={(e) => {
            handleVin(e, rowData, data.field);
          }}
        />
      );
    } else if (data.field === "error" && rowData["error"] === true) {
      let tooltipMessage = rowData["errorMessage"] ? rowData["errorMessage"] : "Error in VIN";
      return (
        <IconWrapper>
          <Tooltip target=".errorlogo" mouseTrack mouseTrackLeft={10} />
          <p className="errorText" style={{ color: "red", marginRight: "3px" }}>
            Invalid
          </p>
          <img
            alt="Error icon"
            className="errorlogo"
            src={FailedIcon}
            height="16px"
            width="16px"
            aria-label="Error"
            data-pr-tooltip={tooltipMessage}
          />
        </IconWrapper>
      );
    } else {
      return <SmallText>{rowData[data.field] === "" ? "--" : rowData[data.field]}</SmallText>;
    }
  };
  const renderEmptyMessage = () => {
    return <SmallText>{noDataMessage}</SmallText>;
  };

  const renderCheckboxes = (rowData, data) => {
    return (
      <div
        css={css`
          padding-left: 5px;
        `}
      >
        <Checkbox
          role="checkbox"
          aria-checked={rowData[data.field]}
          checked={rowData[data.field]}
          onChange={(e) => updateRowData(e, rowData, data.field)}
        ></Checkbox>
      </div>
    );
  };
  const renderHeader = () => {
    return (
      <FlexRow justifyContent="space-between" marginTop="1rem">
        <FlexRow style={{ marginTop: "0.5rem" }}>
          <MediumText>{vinInfo.validVIns} Valid VINs</MediumText>
          <MediumText style={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>{vinInfo.errorVins} Errors</MediumText>
          <Button role="button" size="small" variant="secondary" aria-label="DeleteAllButton" onClick={deleteAll}>
            Delete All
          </Button>
        </FlexRow>
        <FlexRow justifyContent="space-between">
          <SearchInput
            role="search"
            aria-label={globalFilterValue}
            value={globalFilterValue}
            type="text"
            onChange={onGlobalFilterChange}
            placeholder="Search VIN"
          ></SearchInput>
          {updatedVehicles.length > 0 && (
            <Button role="button" size="small" aria-label="addmorevehicles" onClick={addMoreVehicles} disabled={pendingVehicleCount == 0}>
              Add More Vehicles
            </Button>
          )}
          <Button
            style={{ marginLeft: "0.5rem" }}
            role="button"
            size="small"
            variant="secondary"
            aria-label="saveButton"
            disabled={saveDisabled}
            ariaDisabled={saveDisabled}
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </FlexRow>
      </FlexRow>
    );
  };
  return (
    <div>
      <GridWrapper
        colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}
        autoResizeHead={true}
        fontSize="14px"
        colGutter={true}
        whiteSpace={whiteSpace}
        border={border}
      >
        <DataTable
          header={renderHeader()}
          sortMode="multiple"
          removableSort
          ref={dt}
          resizableColumns={resizableColumns}
          columnResizeMode={resizableColumns ? "expand" : "fit"}
          scrollable
          scrollDirection="horizontal"
          responsiveLayout="scroll"
          filterDisplay="menu"
          emptyMessage={renderEmptyMessage()}
          filters={filters}
          value={updatedRows}
          pageLinkSize={3}
        >
          {columns.map((item) => {
            return (
              <Column
                key={item.key}
                field={item.value}
                columnKey={item.key}
                body={getBodyToDisplay}
                header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} />}
                style={{
                  "min-width": `${item.width || "200px"}`,
                  width: `${item.width || "200px"}`,
                  "min-height": "34px",
                  padding: "4px 0 4px 0!important",
                  backgroundColor: flyOutMenu && "transparent",
                  ...item.style,
                }}
                sortable={item.isSortable}
                filter={item.isFilterEnabled}
                frozen={item.isFrozen}
              ></Column>
            );
          })}
          {showActionIcon && <Column body={showActionIcon} />}
        </DataTable>
      </GridWrapper>
    </div>
  );
};
